import { columnOf, SOURCE_LABELS, type Source } from '@jt/core';
import type { ApplicationListItem } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { post, del } from '../api/client';
import {
  keys,
  refreshSummaries,
  useApplications,
  useSettings,
  useSettingsSave,
  useToday,
} from '../api/hooks';
import { AppCard } from '../components/AppCard';
import { AppTable } from '../components/AppTable';
import { Board } from '../components/Board';
import { openNewApplication } from '../components/NewApplication';
import { ConfirmButton, useUI } from '../components/overlays';
import { isKpiKey, kpiMatch, KPI_TITLES, Kpis, TodayBlock } from '../components/Today';
import { Empty, Icon, PageHead, Spinner } from '../components/ui';
import { AI_OFF_HINT } from '../drafts/useDraft';
import { useIntegrations } from './integrations';

/** Кнопки в шапке: демо-данные, «Вставить текст», «Добавить вакансию». */
export function HeadActions({ hasDemo, ai }: { hasDemo: boolean; ai: boolean }) {
  const ui = useUI();
  const qc = useQueryClient();
  return (
    <>
      {hasDemo && (
        <ConfirmButton
          className="badge"
          onConfirm={async () => {
            await del('/demo');
            await qc.invalidateQueries({ queryKey: keys.apps() });
            refreshSummaries(qc);
            ui.toast('Демо-данные убраны');
          }}
        >
          <Icon name="info" size={16} /> Демо-данные · убрать
        </ConfirmButton>
      )}
      <button
        className="btn"
        disabled={!ai}
        title={ai ? undefined : AI_OFF_HINT}
        onClick={() => openNewApplication(ui, { mode: 'text' })}
      >
        <Icon name="clip" /> Вставить текст
      </button>
      <button className="btn primary" onClick={() => openNewApplication(ui)}>
        <Icon name="plus" /> Добавить вакансию
      </button>
    </>
  );
}

export function EmptyStart({ ai }: { ai: boolean }) {
  const ui = useUI();
  const qc = useQueryClient();
  return (
    <Empty
      title="Добавьте первую вакансию"
      text={
        ai
          ? 'Вставьте текст вакансии, письмо рекрутера или пост из Telegram — Claude заполнит карточку. Или добавьте вручную и дальше ведите статусы, контакты и переписку.'
          : 'Добавьте вакансию вручную и дальше ведите статусы, контакты и переписку.'
      }
    >
      <button className="btn primary" onClick={() => openNewApplication(ui)}>
        <Icon name="plus" /> Добавить вакансию
      </button>
      {ai && (
        <button className="btn" onClick={() => openNewApplication(ui, { mode: 'text' })}>
          <Icon name="clip" /> Вставить текст
        </button>
      )}
      <button
        className="btn ghost"
        onClick={async () => {
          await post('/demo');
          await qc.invalidateQueries({ queryKey: keys.apps() });
          refreshSummaries(qc);
          ui.toast('Добавлены демо-данные — их можно убрать одной кнопкой');
        }}
      >
        Показать на примере
      </button>
      <FirstSteps />
    </Empty>
  );
}

/** Первые шаги нового пользователя: профиль, почта, Telegram. */
function FirstSteps() {
  const settings = useSettings();
  const integ = useIntegrations();
  const steps: [boolean, string, string][] = [
    [
      !!settings.data?.about,
      'Расскажите о себе в «Профиле для писем» — Claude опирается на это',
      '/settings#profile',
    ],
    [
      !!integ.data?.gmail.connected,
      'Подключите почту — ответы компаний будут разбираться сами',
      '/settings#gmail',
    ],
    [!!integ.data?.telegram.linked, 'Привяжите Telegram — уведомления и дайджест', '/settings#telegram'],
  ];
  if (steps.every(([done]) => done)) return null;
  return (
    <ul className="first-steps">
      {steps.map(([done, text, to]) => (
        <li key={to} className={done ? 'done' : ''}>
          <Icon name={done ? 'check' : 'chevR'} size={16} /> {done ? text : <Link to={to}>{text}</Link>}
        </li>
      ))}
    </ul>
  );
}

function useDebounced<T>(v: T, ms = 250): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

export function ApplicationsPage() {
  const loc = useLocation();
  const kpiParam = new URLSearchParams(loc.search).get('f');
  const kpi = isKpiKey(kpiParam) ? kpiParam : null;
  const selectedId = matchPath('/applications/:id', loc.pathname)?.params.id ?? null;
  const today = useToday();
  const ai = !!today.data?.ai;
  const settings = useSettings();
  const saveSettings = useSettingsSave();
  const [q, setQ] = useState(() => sessionStorage.getItem('jt.q') ?? '');
  const [src, setSrc] = useState(() => sessionStorage.getItem('jt.src') ?? '');
  const [showArchive, setShowArchive] = useState(false);
  const dq = useDebounced(q);
  useEffect(() => {
    try {
      sessionStorage.setItem('jt.q', q);
      sessionStorage.setItem('jt.src', src);
    } catch {
      /* приватный режим */
    }
  }, [q, src]);

  const all = useApplications();
  const filtered = useApplications({ q: dq, source: src || undefined });
  const view = (settings.data?.ui.board as string | undefined) === 'table' ? 'table' : 'board';

  const list = useMemo(() => filtered.data ?? [], [filtered.data]);
  const active = list.filter((a) => columnOf(a.stage) !== 'archive');
  const arch = list.filter((a) => columnOf(a.stage) === 'archive');
  const sources = [...new Set((all.data ?? []).map((a) => a.source).filter(Boolean))] as Source[];
  const hasDemo = (all.data ?? []).some((a) => a.isDemo);

  const head = (
    <PageHead title="Мои отклики" sub="Каждый отклик — под контролем">
      <HeadActions hasDemo={hasDemo} ai={ai} />
    </PageHead>
  );
  if (all.isPending) return head;
  if (all.isError)
    return (
      <>
        {head}
        <Spinner label="Не удалось загрузить вакансии. Обновите страницу." />
      </>
    );
  if (!all.data.length)
    return (
      <>
        {head}
        <EmptyStart ai={ai} />
      </>
    );

  return (
    <>
      {head}
      <Kpis today={today.data} current={kpi} />
      {kpi ? (
        <KpiList
          title={KPI_TITLES[kpi]}
          list={list.filter((a) => kpiMatch(kpi, a))}
          ai={ai}
          selectedId={selectedId}
        />
      ) : (
        <>
          <TodayBlock today={today.data} />
          <div className="toolbar">
            <div className="seg" role="group" aria-label="Вид">
              <button
                aria-pressed={view === 'board'}
                onClick={() => void saveSettings({ ui: { board: 'board' } }).catch(() => undefined)}
              >
                <Icon name="board" /> Доска
              </button>
              <button
                aria-pressed={view === 'table'}
                onClick={() => void saveSettings({ ui: { board: 'table' } }).catch(() => undefined)}
              >
                <Icon name="table" /> Таблица
              </button>
            </div>
            <label className="search">
              <Icon name="search" size={20} />
              <input
                type="search"
                placeholder="Поиск компании или вакансии…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Поиск"
              />
            </label>
            <label className="selw">
              <Icon name="filter" size={18} />
              <select value={src} onChange={(e) => setSrc(e.target.value)} aria-label="Источник">
                <option value="">Все источники</option>
                {sources.map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_LABELS[s]}
                  </option>
                ))}
              </select>
              <Icon name="chevD" />
            </label>
          </div>
          {view === 'table' ? (
            active.length ? (
              <AppTable list={active} selectedId={selectedId} />
            ) : (
              <p className="muted">Ничего не найдено.</p>
            )
          ) : (
            <Board list={active} ai={ai} selectedId={selectedId} />
          )}
          {arch.length > 0 && (
            <section className="arch">
              <button
                className="arch-h"
                onClick={() => setShowArchive(!showArchive)}
                aria-expanded={showArchive}
              >
                <Icon name="archive" size={20} /> Архив и отказы · {arch.length}{' '}
                <Icon name={showArchive ? 'chevD' : 'chevR'} size={16} />
              </button>
              {showArchive && (
                <div className="arch-grid">
                  {arch.map((a) => (
                    <AppCard key={a.id} a={a} ai={ai} selected={selectedId === a.id} />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </>
  );
}

/** Карточки одного счётчика («Ждут ответа» и т. п.). */
function KpiList({
  title,
  list,
  ai,
  selectedId,
}: {
  title: string;
  list: ApplicationListItem[];
  ai: boolean;
  selectedId: string | null;
}) {
  return (
    <section className="box">
      <div className="boxh">
        <h2>
          {title} · {list.length}
        </h2>
        <Link className="btn sm" to="/applications">
          Все вакансии
        </Link>
      </div>
      {list.length ? (
        <div className="arch-grid">
          {list.map((a) => (
            <AppCard key={a.id} a={a} ai={ai} selected={selectedId === a.id} />
          ))}
        </div>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          Здесь пока пусто.
        </p>
      )}
    </section>
  );
}
